import { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import api from '../../../services/api';
import {
  Paper,
  IconButton,
  Text,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Select,
  MenuItem,
  Div,
  Spacer,
  TextField,
  Divider
} from '../../core';
import { SecctionContainer, SeccionHeader, AlertDialog } from '../../patterns';
import { NotificationNav } from '../../patterns/NotificationNav';
import { EditIcon } from '../../core/Icons';
import { AppContext } from '../../../contexts/AppContext';
import { LessonPaginator } from '../../patterns/lesson/LessonPaginator';

export default function UserGroupsList() {
  const { setLoading } = useContext(AppContext);
  const [userGroups, setUserGroups] = useState([]);
  const [query, setQuery] = useState('');
  const [firstLoad, setFirstLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    count: 0,
    sort: 'asc',
    sortField: 'name'
  });

  const [alert, setAlert] = useState({
    show: false,
    errors: [{ message: 'Ocorreu um erro' }]
  });

  const loadUserGroups = useCallback(({ localPagination = pagination }) => {
    setLoading(true);
    const queryParam = query ? `&q=name:like:${query}` : '';
    const sortPrefix = localPagination.sort === 'desc' ? '-' : '';

    api
      .get(
        `/admin/user-groups?page=${localPagination.page}&per_page=${localPagination.per_page}&sort=${sortPrefix}${localPagination.sortField}${queryParam}`
      )
      .then((res) => {
        setPagination((prev) => ({
          ...prev,
          count: res.data.pagination?.count || 0
        }));
        setLoading(false);
        setUserGroups(res.data.usergroups || []);
      })
      .catch((err) => {
        setLoading(false);
        setUserGroups([]);
        setAlert({
          errors: err.response?.data?.errors || [{ message: 'Erro ao carregar grupos de usuários' }],
          show: true
        });
      });
  }, [setLoading, query, pagination]);

  useEffect(() => {
    const stored = window.localStorage.getItem('userGroupsPagination');
    if (stored) {
      const localPagination = JSON.parse(stored);
      setPagination(localPagination);
      loadUserGroups({ localPagination });
    } else {
      loadUserGroups({ localPagination: pagination });
    }
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!firstLoad) {
      window.localStorage.setItem('userGroupsPagination', JSON.stringify(pagination));
      loadUserGroups({ localPagination: pagination });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.per_page, pagination.sort]);

  const history = useHistory();

  const handleEdit = useCallback((id) => history.push(`/app/notifications/user-groups/edit/${id}`), [history]);
  const handleNew = useCallback(() => history.push('/app/notifications/user-groups/edit/new'), [history]);

  const onPerPageChange = ({ target }) => {
    setPagination({ ...pagination, per_page: target.value, page: 1 });
  };

  const onPageChange = (event, newPage) => {
    setPagination({ ...pagination, page: newPage + 1 });
  };

  const handleSortChange = ({ target }) => {
    setPagination({ ...pagination, sort: target.value });
  };

  const handleQueryChange = ({ target }) => {
    setQuery(target.value);
  };

  return (
    <SecctionContainer>
      <NotificationNav />
      <SeccionHeader title="Grupos de Usuários" newButtonAction={handleNew} newButton />
      <AlertDialog
        open={alert.show}
        handleClose={() => setAlert({ ...alert, show: false })}
        errors={alert.errors}
      />
      <TableContainer component={Paper}>
        <Div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
          <TextField
            label="Busca"
            variant="outlined"
            value={query}
            fullWidth
            autoFocus
            name="queryUserGroup"
            placeholder="Buscar por nome do grupo"
            onChange={handleQueryChange}
            onKeyPress={({ key }) => {
              if (key === 'Enter') loadUserGroups({ localPagination: pagination });
            }}
            style={{ marginTop: 20, marginLeft: 20 }}
          />
          <Spacer />
          <Select
            id="sort"
            value={pagination.sort}
            handleChange={handleSortChange}
            label="Ordem"
            name="sort"
            style={{ marginLeft: 20, marginRight: 20, maxWidth: 200 }}
          >
            <MenuItem value="asc">Nome (A-Z)</MenuItem>
            <MenuItem value="desc">Nome (Z-A)</MenuItem>
          </Select>
        </Div>
        <LessonPaginator
          pagination={pagination}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />
        <Divider />
        <Table size="medium">
          <TableBody>
            {userGroups.map((group) => (
              <TableRow key={group.id} hover>
                <TableCell
                  onClick={() => handleEdit(group.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <Text style={{ fontWeight: 700 }}>{group.name}</Text>
                  {group.description && (
                    <Text style={{ color: '#666', fontSize: '0.875rem' }}>{group.description}</Text>
                  )}
                </TableCell>
                <TableCell>
                  <Text style={{ color: '#666', fontSize: '0.875rem' }}>
                    {group.users?.length || 0} membro(s)
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(group.id)}
                    title="Editar grupo"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {userGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Text style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                    Nenhum grupo encontrado
                  </Text>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SecctionContainer>
  );
}
